import { BasicEvent } from "@h4x/common";
import { normalizeCode } from "./HewSyncUtils";
import { HewSync, type HewSyncTypeConfig } from "./HewSync";
import { derived, get, type Writable, writable } from "svelte/store";
// import { HEWYNC_QUEUE } from "../../../ui-components/src/stores/App";

// let HewSyncQueue = HEWSYNC_QUEUE as Writable<any[]>;

interface BaseSubscriptionEvent {
	readonly $event: string;
}

interface CreatedEvent extends BaseSubscriptionEvent {
	readonly $event: "Created";
	readonly type: string;
}

interface KeyUpdatedEvent extends BaseSubscriptionEvent {
	readonly $event: "KeyUpdated";
	readonly old: any;
	readonly updated: any;
}

interface UpdatedEvent extends BaseSubscriptionEvent {
	readonly $event: "Updated";
	readonly type: string;
}

interface RemovedEvent extends BaseSubscriptionEvent {
	readonly $event: "Removed";
	readonly type: string;
}

interface QueueEvent extends BaseSubscriptionEvent {
	readonly $event: "Queue";
	readonly type: string;
}

interface QueueUpdateEvent extends BaseSubscriptionEvent {
	readonly $event: "QueueUpdate";
	readonly id: string;
	readonly type: string;
	readonly completed: number;
	readonly total: number;
	readonly error?: string | null;
	readonly status?: string | null;
}

interface PermissionsUpdatedEvent extends BaseSubscriptionEvent {
	readonly $event: "PermissionsUpdated";
	readonly organization: string;
	readonly account: string;
	readonly permissions: any;
}

interface PermissionsRemovedEvent extends BaseSubscriptionEvent {
	readonly $event: "PermissionsRemoved";
	readonly organization: string;
	readonly account: string;
}

export type SubscriptionEvent =
	| CreatedEvent
	| UpdatedEvent
	| KeyUpdatedEvent
	| RemovedEvent
	| QueueEvent
	| QueueUpdateEvent
	| PermissionsUpdatedEvent
	| PermissionsRemovedEvent;

interface BaseSessionResponse {
	readonly message: string | null;
}

interface DataSessionResponse extends BaseSessionResponse {
	readonly data: {
		url: string;
		accountID: string;
	};
	readonly error: null;
}

interface InvalidTokenTypeSessionResponse extends BaseSessionResponse {
	readonly data: null;
	readonly error: "InvalidTokenType";
}
interface FailedToCreateAccountSessionResponse extends BaseSessionResponse {
	readonly data: null;
	readonly error: {
		readonly FailedToCreateAccount: string;
	};
}

interface FailedToUpdateAccountSessionResponse extends BaseSessionResponse {
	readonly data: null;
	readonly error: {
		readonly FailedToUpdateAccount: string;
	};
}

interface CorruptAccountSessionResponse extends BaseSessionResponse {
	readonly data: null;
	readonly error: {
		readonly CorruptAccount: {
			readonly reason: string;
		};
	};
}

export type SessionResponse =
	| DataSessionResponse
	| InvalidTokenTypeSessionResponse
	| FailedToCreateAccountSessionResponse
	| FailedToUpdateAccountSessionResponse
	| CorruptAccountSessionResponse;

export type ErrorSessionResponse =
	| InvalidTokenTypeSessionResponse
	| FailedToCreateAccountSessionResponse
	| FailedToUpdateAccountSessionResponse
	| CorruptAccountSessionResponse
	| { error: "Connection Error" };

export type HewSyncConnectionState =
	| "connected"
	| "disconnected"
	| "connecting"
	| "connection-error"
	| "error"
	| "not-authenticated";

export const HewSyncConnectedState: HewSyncConnectionState[] = ["connected"];
export const HewSyncWarningState: HewSyncConnectionState[] = ["connecting", "error"];
export const HewSyncDisconnectedState: HewSyncConnectionState[] = [
	"disconnected",
	"not-authenticated",
	"connection-error"
];

export class HewSyncSocket {
	private auth?: string;
	private id_token: string;
	private accountID?: string;

	private disabled = false;
	private baseUrl: string;

	public connectionState = writable<HewSyncConnectionState>("connecting");
	public error = writable<ErrorSessionResponse | null>(null);

	public queue = writable<any[]>([]);
	public queueCount = derived(this.queue, ($queue) => {
		return $queue.length;
	});

	constructor() {}

	public disable() {
		this.disabled = true;
	}

	private ws: WebSocket | undefined;

	public async connect(auth?: string, connectionUrl: string = "", id_token?: string) {
		console.debug("[HewSyncSocket] Called HewSyncSocket.connect().");

		this.baseUrl = connectionUrl;

		if (auth && this.auth !== auth) {
			this.auth = auth;

			if (this.ws) {
				this.ws?.close();
				console.debug("[HewSyncSocket] Closing old connection");
				this.ws = undefined;
			} else {
				console.debug("[HewSyncSocket] WS was not actually started.");
			}
		}

		// Should be moved
		if (id_token && this.id_token !== id_token) {
			this.id_token = id_token;
		}

		if (this.ws) {
			console.debug("[HewSyncSocket] Already connected");
			return this.accountID;
		}

		if (this.auth === undefined) {
			this.connectionState.set("not-authenticated");
			throw new Error("Not authenticated");
		}

		this.ws = new WebSocket(`${this.baseUrl}`);
		this.ws.addEventListener("open", (event) => {
			this.connectionState.set("connected");
			HewSync.onConnectionUpdate.execute();
			this.onOpen();
		});

		this.ws.addEventListener("message", (event) => {
			try {
				this.onMessage(JSON.parse(event.data as string) as { type: string; [id: string]: any });
			} catch (e) {
				console.error(e);
			}
		});

		// TS dose not know what target.url is.
		this.ws.addEventListener("close", (e: any) => {
			this.error.set({ error: "Connection Error" });
			console.error("[HewSyncSocket] Connection closed.");

			const targetUrl = e.target?.url.split("//")[1];
			const baseUrl = this.baseUrl.split("//")[1];

			if (targetUrl === baseUrl) {
				console.debug("[HewSyncSocket] Connection closed, setting state if target.url is not baseUrl.");
				this.connectionState.set("disconnected");
			}

			HewSync.onConnectionUpdate.execute();
		});

		this.connectionState.set("connected");

		HewSync.onConnectionUpdate.execute();

		return this.accountID;
	}

	private responseMap = new Map<
		string,
		{
			resolve: (data: any) => void;
			reject: (error: any) => void;
		}
	>();
	public async request<T>(data: { type: string; [id: string]: any }) {
		await this.authorized;

		/*
			pub(crate) id:      String,
			pub(crate) org:     Option<String>,
			// #[serde(flatten)]
			pub(crate) request: HewSyncRequest,
		*/
		let id = crypto.randomUUID();
		let send_promise = this.send({
			type: "HewSync",
			payload: {
				id,
				request: data
			}
		});

		let promise = new Promise<T>((resolve, reject) => {
			this.responseMap.set(id, { resolve, reject });
		});

		await send_promise;
		return promise;
	}

	public proxy(sender: (data: string) => void, receiver: BasicEvent<(data: string) => void>) {
		this.ws = {
			send: (data: string) => {
				sender(data);
			},
			close: () => {}
		} as any as WebSocket;

		receiver.addCallback((data) => {
			this.onMessage(JSON.parse(data) as { type: string; [id: string]: any });
		});

		this.authorized = Promise.resolve(true);
	}

	private onOpen() {
		console.debug("[HewSyncSocket] Successfully connected");
		// this.send({ type: "connection_init", payload: {} });
		// {"type":"connection_ack","payload":{"connectionTimeoutMs":300000}}
		this.authorizedResolve?.(true);
		this.authorized = true;
		this.connectionState.set("connected");
	}

	private authorizedResolve: ((result: true) => void) | undefined = undefined;
	private authorized: Promise<true> | true | undefined = new Promise((resolve) => {
		this.authorizedResolve = resolve;
	});
	private onMessage(data: any) {
		if (data.type === "data") {
		} else if (data.type === "HewSync") {
			let response = this.responseMap.get(data.id);
			if (response) {
				response.resolve(data);
				this.responseMap.delete(data.id);
			}
		} else if (data.type === "HewSyncEvent") {
			console.debug("[HewSyncSocket] Event", data.data);
			let event = data.data as SubscriptionEvent;

			if (event.$event === "Created") {
				let subscriptions = this.subscriptions.get(event.type);
				if (subscriptions) {
					subscriptions.callback.execute(event);
				}
			} else if (event.$event === "Updated") {
				let subscriptions = this.subscriptions.get(event.type);
				if (subscriptions) {
					subscriptions.callback.execute(event);
				}
			} else if (event.$event === "KeyUpdated") {
				let subscriptions = this.subscriptions.get(event.updated.type);
				if (subscriptions) {
					subscriptions.callback.execute(event);
				}
			} else if (event.$event === "Removed") {
				console.debug("[HewSyncSocket] Removed", data.data);
				let subscriptions = this.subscriptions.get(event.type);
				if (subscriptions) {
					console.debug("[HewSyncSocket] subscriptions", data.data);
					subscriptions.callback.execute(event);
				}
			} else if (event.$event === "Queue") {
				if (data.data["full_queue"]) {
					console.debug("[HewSyncSocket] Full queue", data.data["full_queue"]);
					this.queue.update((old) => {
						let newQueue = data.data["full_queue"];
						// HewSyncQueue = data.data["full_queue"];
						return newQueue;
					});
				} else {
					this.queue.update((old) => {
						let newQueue = [...old, data.data["queue"]];
						// HewSyncQueue.push(data.data["queue"]);
						return newQueue;
					});
				}

				// console.log("[HewSyncSocket] Queue:", this.queue);
			} else if (event.$event === "QueueUpdate") {
				this.queue.update((old) => {
					return old.map((item) => {
						if (item.id === event.id) {
							return {
								...item,
								completed: event.completed,
								total: event.total,
								error: event.error,
								status: event.status
							};
						}
						return item;
					});
				});
			} else if (event.$event === "PermissionsUpdated") {
				console.error("[HewSyncSocket] PermissionsUpdated", event);
			} else if (event.$event === "PermissionsRemoved") {
				console.error("[HewSyncSocket] PermissionsRemoved", event);
			} else {
				console.warn("[HewSyncSocket] Unknown event type", event);
			}
		} else if (data.type === "Error") {
			if (data.payload?.errors) {
				console.error("[HewSyncSocket] Error", ...data.payload.errors);
			} else {
				console.error("[HewSyncSocket] Error", data.error);
			}
		}
	}

	private subscriptions = new Map<
		string,
		{
			callback: BasicEvent<(data: any) => void>;
		}
	>();
	public async subscribe<T>(name: string, callback: (data: T) => void) {
		await HewSync.authPromise;
		if (this.disabled) {
			this.connectionState.set("disconnected");
			return;
		}
		if (this.authorized === undefined) {
			throw new Error("Not connected");
		} else if (this.authorized !== true) {
			await this.authorized;
		}

		let subscriptions = this.subscriptions.get(name);
		if (!subscriptions) {
			console.debug("[HewSyncSocket] Creating subscription", name);
			subscriptions = {
				callback: new BasicEvent()
			};
			this.subscriptions.set(name, subscriptions);
		}

		subscriptions.callback.addCallback(callback);
	}

	private async send(data: any) {
		if (!this.ws) {
			throw new Error("Not connected");
		}

		console.debug("[HewSyncSocket] Sending", data);

		if (this.ws.readyState !== WebSocket.OPEN) {
			await new Promise<void>((resolve, reject) => {
				const timeout = setTimeout(() => {
					reject(new Error("Websocket connection timeout"));
				}, 5000);

				this.ws?.addEventListener(
					"open",
					() => {
						clearTimeout(timeout);
						resolve();
					},
					{ once: true }
				);

				this.ws?.addEventListener(
					"error",
					(e) => {
						console.error("[HewSyncSocket] Websocket connection error in this.send()", e);
						clearTimeout(timeout);
						reject(new Error("Websocket connection error"));
					},
					{ once: true }
				);
			});
		}

		if (!this.isConnected) {
			this.connectionState.set("disconnected");
		}

		this.ws.send(JSON.stringify(data));
	}

	public async clearQueue() {
		console.debug("[HewSyncSocket] Clearing queue");
		let currentQueue = get(this.queue);

		if (currentQueue.length === 0) {
			console.debug("[HewSyncSocket] Queue is already empty");
			return;
		}
		currentQueue = currentQueue.filter((item) => item.status !== "completed");
		this.queue.set(currentQueue);
	}

	public async removeFromQueue(id: string) {
		console.debug(`[HewSyncSocket] Removing item with id "${id}" if complete`);
		let currentQueue = get(this.queue);

		const updatedQueue = currentQueue.filter((item) => !(item.id === id && item.status === "completed"));

		if (updatedQueue.length === currentQueue.length) {
			console.debug(`[HewSyncSocket] No completed item with id "${id}" found`);
			return;
		}

		this.queue.set(updatedQueue);
	}

	public get isConnected() {
		return this.ws !== undefined && this.ws.readyState !== WebSocket.CLOSED;
	}
}
